/**
 * Kleda Client App — Risico Keuze Cards (Fase 2)
 *
 * Toont 4 gelijkwaardige opties per WI als radio-cards:
 *   1. Ja, ga gerust door (geen kosten)
 *   2. Onbehandeld retour (€XX of €0 bij multi-WI)
 *   3. Zelfhalen in Havelte
 *   4. Afvoeren vloerkleed
 *
 * Vervangt de eerdere "Ja-knop + expander"-flow. Reden: gebruiker moest
 * 2 keer klikken (Ja-keuze + Submit), en de Ja-knop oogde als directe
 * actie. Met radio-cards is alles één scan-en-kies, en is Submit het
 * enige actie-blok.
 */

export interface Alternatief {
  productCode: string
  productNaam: string | null
  prijs: number
  isMultiWiKorting: boolean
  beschrijving: string
}

export type Choice =
  | { kind: 'akkoord' }
  | { kind: 'alternatief'; productCode: string }
  | null

interface Props {
  wasserijItemId: string
  alternatives: Alternatief[]
  selected: Choice
  onChoose: (choice: Choice) => void
  disabled?: boolean
}

function formatPrijs(prijs: number): string {
  if (prijs <= 0) return 'Geen extra kosten'
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(prijs)
}

interface CardProps {
  groupName: string
  isSelected: boolean
  title: string
  beschrijving: string
  prijsLabel: string
  prijsSubLabel?: string | null
  onSelect: () => void
  disabled?: boolean
  recommended?: boolean
  /** Toont prijs-label in grijs i.p.v. zwart, voor neutrale labels
   *  als "Zoals afgesproken" zodat het visueel niet als prijs leest. */
  prijsMuted?: boolean
}

function Card({
  groupName,
  isSelected,
  title,
  beschrijving,
  prijsLabel,
  prijsSubLabel,
  onSelect,
  disabled,
  recommended,
  prijsMuted,
}: CardProps) {
  return (
    <label
      className={
        'block cursor-pointer rounded-card border p-md transition-colors ' +
        (disabled ? 'opacity-50 cursor-not-allowed ' : '') +
        (isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border-subtle bg-white hover:border-primary/40')
      }
    >
      <input
        type="radio"
        name={groupName}
        checked={isSelected}
        onChange={onSelect}
        disabled={disabled}
        className="sr-only"
      />
      <div className="flex items-start justify-between gap-sm">
        <div className="flex items-start gap-sm flex-1">
          <span
            className={
              'mt-1 inline-block w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ' +
              (isSelected
                ? 'border-primary bg-primary'
                : 'border-border-subtle bg-white')
            }
            aria-hidden="true"
          >
            {isSelected && (
              <span className="block w-2 h-2 rounded-full bg-white" />
            )}
          </span>
          <div className="flex-1 space-y-xs">
            <div className="flex items-center gap-xs flex-wrap">
              <span className="text-body font-semibold text-ink">
                {title}
              </span>
              {recommended && (
                <span className="text-body-sm font-medium text-primary bg-primary/10 px-sm py-xs rounded-full">
                  Aanbevolen
                </span>
              )}
            </div>
            <p className="text-body-sm text-ink-muted leading-relaxed">
              {beschrijving}
            </p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <p
            className={
              prijsMuted
                ? 'text-body-sm text-ink-muted font-normal'
                : 'text-body font-semibold text-ink'
            }
          >
            {prijsLabel}
          </p>
          {prijsSubLabel && (
            <p className="text-body-sm text-primary mt-xs">{prijsSubLabel}</p>
          )}
        </div>
      </div>
    </label>
  )
}

export function RisicoKeuzeCards({
  wasserijItemId,
  alternatives,
  selected,
  onChoose,
  disabled,
}: Props) {
  const groupName = `risico-keuze-${wasserijItemId}`

  const isAkkoord = selected?.kind === 'akkoord'
  const altSelected =
    selected?.kind === 'alternatief' ? selected.productCode : null

  return (
    <div role="radiogroup" aria-label="Wat wil je dat we doen?" className="space-y-sm">
      {/* Optie 1: Ja, ga gerust door — geen prijs-label want dat is gewoon
          de oorspronkelijke afspraak (anders lijkt deze optie "goedkoper") */}
      <Card
        groupName={groupName}
        isSelected={isAkkoord}
        title="Ja, ga gerust door"
        beschrijving="We gaan je vloerkleed reinigen, met de aandachtspunten in gedachten. Je hoort het van ons als je kleed klaar is."
        prijsLabel="Zoals afgesproken"
        onSelect={() => onChoose({ kind: 'akkoord' })}
        disabled={disabled}
        recommended
        prijsMuted
      />

      {/* Opties 2-4: alternatieven uit server-driven config */}
      {alternatives.map((alt) => {
        const multi = alt.isMultiWiKorting
        return (
          <Card
            key={alt.productCode}
            groupName={groupName}
            isSelected={altSelected === alt.productCode}
            title={alt.productNaam || alt.productCode}
            beschrijving={alt.beschrijving}
            prijsLabel={formatPrijs(alt.prijs)}
            prijsSubLabel={multi ? 'Gaat mee met je andere kleed' : null}
            onSelect={() =>
              onChoose({ kind: 'alternatief', productCode: alt.productCode })
            }
            disabled={disabled}
          />
        )
      })}

      {/* Fallback als alternatives leeg zijn (data-issue / oude WI) */}
      {alternatives.length === 0 && (
        <p className="text-body-sm text-ink-muted text-center py-sm">
          Heb je liever een ander alternatief? Bel ons op{' '}
          <a href="tel:+31882999277" className="text-primary underline">
            088 299 9277
          </a>
          .
        </p>
      )}
    </div>
  )
}
